import React, { useState } from 'react';
import { Calculator, MapPin, Users, Clock, Info, AlertCircle, Navigation, Route, Timer, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdvancedPriceCalculator = () => {
  const [formData, setFormData] = useState({
    pickupAddress: '',
    destination: '',
    tripType: 'short',
    isWithinHagen: true,
    isSenior: false,
    roundTrip: false,
    waitingService: false,
    waitingServiceDuration: 0,
    infectiousAnimal: false,
    isAnimalWelfare: false
  });
  
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCalculate = async () => {
    if (!formData.pickupAddress || !formData.destination) {
      toast.error('Bitte geben Sie Abhol- und Zieladresse ein');
      return;
    }

    setIsCalculating(true);
    
    try {
      const response = await axios.post(`${API}/calculator/calculate-price-advanced`, formData);
      setCalculatedPrice(response.data);
    } catch (error) {
      console.error('Fehler beim Berechnen:', error);
      toast.error('Fehler bei der Preisberechnung');
    } finally {
      setIsCalculating(false);
    }
  };

  // Generate Google Maps URL for route (shortest distance)
  const getGoogleMapsUrl = () => {
    const baseAddress = 'Sternstraße 1, 58089 Hagen, Germany';
    const isLongDistance = formData.tripType === 'long';
    
    // dirflg=d ensures driving directions
    if (isLongDistance) {
      // Long distance: Base -> Pickup -> Destination -> Base
      return `https://www.google.com/maps/dir/${encodeURIComponent(baseAddress)}/${encodeURIComponent(formData.pickupAddress)}/${encodeURIComponent(formData.destination)}/${encodeURIComponent(baseAddress)}?dirflg=d`;
    } else {
      // Short distance: Pickup -> Destination
      return `https://www.google.com/maps/dir/${encodeURIComponent(formData.pickupAddress)}/${encodeURIComponent(formData.destination)}?dirflg=d`;
    }
  };

  // Generate static map image URL
  const getStaticMapUrl = () => {
    const isLongDistance = formData.tripType === 'long';
    const baseAddress = 'Sternstraße 1, 58089 Hagen, Germany';
    
    // Use markers for key points
    let markers = '';
    if (isLongDistance) {
      markers = `&markers=color:green|label:S|${encodeURIComponent(baseAddress)}` +
                `&markers=color:blue|label:A|${encodeURIComponent(formData.pickupAddress)}` +
                `&markers=color:red|label:Z|${encodeURIComponent(formData.destination)}`;
    } else {
      markers = `&markers=color:green|label:A|${encodeURIComponent(formData.pickupAddress)}` +
                `&markers=color:red|label:Z|${encodeURIComponent(formData.destination)}`;
    }
    
    return `https://maps.googleapis.com/maps/api/staticmap?size=600x300&maptype=roadmap${markers}&key=AIzaSyA0futJSwpujm19fwF5yS1M8axT3BKbR58`;
  };

  const tripTypes = [
    { value: 'short', label: 'Kurzstrecke', description: '< 150 km · 2,20€/km' },
    { value: 'long', label: 'Langstrecke', description: '≥ 150 km · 45€/Std oder 0,40€/km (Tierschutz)' },
    { value: 'emergency', label: 'Notfall', description: 'Pauschalpreis' }
  ];

  return (
    <section id="calculator" className="py-20 bg-gradient-to-br from-blue-50 via-white to-orange-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl sm:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-blue-600 to-orange-500 bg-clip-text text-transparent">
              Preisrechner
            </span>
          </h2>
          <p className="text-xl text-gray-600">
            Berechnen Sie die Kosten für Ihren Transport
          </p>
          <p className="text-sm text-gray-500 mt-2">
            <strong>Kurzstrecke (&lt; 150 km):</strong> Nur Fahrstrecke (Abholung → Ziel)<br />
            <strong>Langstrecke (≥ 150 km):</strong> Gesamte Route (Sternstraße 1 → Abholung → Ziel → Sternstraße 1)<br />
            <span className="text-xs">Distanzberechnung via Google Maps Routenplaner</span>
          </p>
        </div>

        <Card className="shadow-xl border-2 border-gray-200">
          <CardHeader className="bg-gradient-to-r from-blue-600 to-orange-500 text-white rounded-t-lg">
            <div className="flex items-center space-x-3">
              <Calculator className="h-8 w-8" />
              <div>
                <CardTitle className="text-2xl">Kosten berechnen</CardTitle>
                <CardDescription className="text-blue-50">
                  Geben Sie die Details Ihrer Fahrt ein
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            {/* Trip Type Selection */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Transportart</Label>
              <div className="grid sm:grid-cols-3 gap-3">
                {tripTypes.map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => handleInputChange('tripType', type.value)}
                    className={`p-4 rounded-xl border-2 text-left transition-all duration-200 ${
                      formData.tripType === type.value
                        ? 'border-blue-500 bg-blue-50 shadow-md'
                        : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="font-semibold text-gray-900">{type.label}</div>
                    <div className="text-xs text-gray-600 mt-1">{type.description}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Addresses */}
            <div className="space-y-2">
              <Label htmlFor="pickupAddress" className="text-base font-semibold flex items-center space-x-2">
                <MapPin className="h-4 w-4 text-blue-600" />
                <span>Abholadresse</span>
              </Label>
              <Input
                id="pickupAddress"
                value={formData.pickupAddress}
                onChange={(e) => handleInputChange('pickupAddress', e.target.value)}
                placeholder="z.B. Musterstraße 10, 58095 Hagen"
                className="text-base h-12"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="destination" className="text-base font-semibold flex items-center space-x-2">
                <MapPin className="h-4 w-4 text-orange-600" />
                <span>Zieladresse</span>
              </Label>
              <Input
                id="destination"
                value={formData.destination}
                onChange={(e) => handleInputChange('destination', e.target.value)}
                placeholder="z.B. Tierarzt Müller, 58093 Hagen"
                className="text-base h-12"
              />
            </div>

            {/* Options for Short Trips */}
            {formData.tripType === 'short' && (
              <>
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center space-x-2">
                    <MapPin className="h-5 w-5 text-blue-600" />
                    <Label htmlFor="within-hagen" className="text-base font-medium cursor-pointer">
                      Innerhalb Hagen?
                    </Label>
                  </div>
                  <Switch
                    id="within-hagen"
                    checked={formData.isWithinHagen}
                    onCheckedChange={(checked) => handleInputChange('isWithinHagen', checked)}
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center space-x-2">
                    <Users className="h-5 w-5 text-orange-600" />
                    <Label htmlFor="senior" className="text-base font-medium cursor-pointer">
                      Rentner-Tarif (1,80€/km)
                    </Label>
                  </div>
                  <Switch
                    id="senior"
                    checked={formData.isSenior}
                    onCheckedChange={(checked) => handleInputChange('isSenior', checked)}
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <div>
                    <Label htmlFor="roundTrip" className="text-base font-medium cursor-pointer">
                      Rückfahrt gewünscht
                    </Label>
                    <p className="text-sm text-gray-600">Auch die Rückfahrt buchen</p>
                  </div>
                  <Switch
                    id="roundTrip"
                    checked={formData.roundTrip}
                    onCheckedChange={(checked) => handleInputChange('roundTrip', checked)}
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <div>
                    <Label htmlFor="waitingService" className="text-base font-medium cursor-pointer">
                      Warteservice (15€/30 Min)
                    </Label>
                    <p className="text-sm text-gray-600">Wartezeit vor Ort</p>
                  </div>
                  <Switch
                    id="waitingService"
                    checked={formData.waitingService}
                    onCheckedChange={(checked) => handleInputChange('waitingService', checked)}
                  />
                </div>

                {formData.waitingService && (
                  <div className="space-y-2">
                    <Label htmlFor="waitingDuration">Voraussichtliche Wartezeit</Label>
                    <Select 
                      value={formData.waitingServiceDuration.toString()} 
                      onValueChange={(value) => handleInputChange('waitingServiceDuration', parseInt(value))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Wartezeit auswählen" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="30">30 Minuten (15€)</SelectItem>
                        <SelectItem value="60">60 Minuten (30€)</SelectItem>
                        <SelectItem value="90">90 Minuten (45€)</SelectItem>
                        <SelectItem value="120">120 Minuten (60€)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </>
            )}

            {/* Options for Long Trips */}
            {formData.tripType === 'long' && (
              <>
                <div className="flex items-center justify-between p-4 bg-green-50 rounded-xl">
                  <div className="flex items-center space-x-2">
                    <Users className="h-5 w-5 text-green-600" />
                    <Label htmlFor="animalWelfare" className="text-base font-medium cursor-pointer">
                      Tierschutzverein (0,40€/km)
                    </Label>
                  </div>
                  <Switch
                    id="animalWelfare"
                    checked={formData.isAnimalWelfare}
                    onCheckedChange={(checked) => handleInputChange('isAnimalWelfare', checked)}
                  />
                </div>
              </>
            )}

            {/* Infectious Animal Warning */}
            <div className="flex items-center justify-between p-4 bg-orange-50 border-2 border-orange-200 rounded-xl">
              <div>
                <Label htmlFor="infectiousAnimal" className="text-base font-medium cursor-pointer flex items-center text-orange-900">
                  <AlertCircle className="h-5 w-5 mr-2 text-orange-600" />
                  Infektiöses Tier (nach Absprache)
                </Label>
                <p className="text-sm text-orange-700">
                  +200€ Desinfektions- und Ausfallkosten (24 Std)
                </p>
              </div>
              <Switch
                id="infectiousAnimal"
                checked={formData.infectiousAnimal}
                onCheckedChange={(checked) => handleInputChange('infectiousAnimal', checked)}
              />
            </div>

            <Button
              onClick={handleCalculate}
              className="w-full h-14 text-lg font-semibold bg-gradient-to-r from-blue-600 to-orange-500 hover:from-blue-700 hover:to-orange-600 transition-all duration-300"
              disabled={isCalculating}
            >
              <Calculator className="h-5 w-5 mr-2" />
              {isCalculating ? 'Wird berechnet...' : 'Preis berechnen'}
            </Button>

            {calculatedPrice && (
              <div className="mt-6 space-y-4 animate-fade-in">
                {/* Price Display */}
                <div className="p-6 bg-gradient-to-r from-blue-600 to-orange-500 rounded-xl text-white">
                  <div className="text-sm font-medium mb-2">Geschätzter Preis</div>
                  <div className="text-5xl font-bold mb-4">{calculatedPrice.price}€</div>
                  
                  {calculatedPrice.details && (
                    <div className="text-sm opacity-90 border-t border-white/30 pt-3">
                      {calculatedPrice.details.routeInfo && (
                        <p className="text-xs mb-2">{calculatedPrice.details.routeInfo}</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Route Details Card */}
                {calculatedPrice.details && (
                  <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
                    {/* Google Maps Link Button */}
                    <a 
                      href={getGoogleMapsUrl()} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="block p-4 bg-gradient-to-r from-blue-50 to-green-50 border-b-2 border-gray-200 hover:from-blue-100 hover:to-green-100 transition-colors"
                    >
                      <div className="flex items-center justify-center gap-3">
                        <Navigation className="h-6 w-6 text-blue-600" />
                        <span className="font-semibold text-gray-800">Route in Google Maps anzeigen</span>
                        <ExternalLink className="h-4 w-4 text-gray-500" />
                      </div>
                      <p className="text-xs text-gray-500 text-center mt-1">
                        Klicken Sie hier, um die detaillierte Route auf Google Maps zu sehen
                      </p>
                    </a>
                    
                    {/* Route Information */}
                    <div className="p-4 space-y-4">
                      <h4 className="font-bold text-lg flex items-center gap-2 text-gray-900">
                        <Route className="h-5 w-5 text-blue-600" />
                        Routendetails
                      </h4>
                      
                      {/* Summary Stats */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-blue-50 rounded-lg p-3 text-center">
                          <div className="flex items-center justify-center gap-2 text-blue-600 mb-1">
                            <Navigation className="h-4 w-4" />
                            <span className="text-sm font-medium">Gesamtstrecke</span>
                          </div>
                          <p className="text-2xl font-bold text-blue-700">
                            {calculatedPrice.details.totalDistance || calculatedPrice.details.tripDistance || '-'} km
                          </p>
                        </div>
                        <div className="bg-orange-50 rounded-lg p-3 text-center">
                          <div className="flex items-center justify-center gap-2 text-orange-600 mb-1">
                            <Timer className="h-4 w-4" />
                            <span className="text-sm font-medium">Fahrzeit</span>
                          </div>
                          <p className="text-2xl font-bold text-orange-700">
                            {calculatedPrice.details.totalDurationText || calculatedPrice.details.tripDurationText || 
                             (calculatedPrice.details.tripDuration ? `ca. ${calculatedPrice.details.tripDuration} Min` : '-')}
                          </p>
                        </div>
                      </div>

                      {/* Route Breakdown for Long Distance */}
                      {calculatedPrice.details.routeBreakdown && (
                        <div className="space-y-2">
                          <h5 className="font-semibold text-gray-700 text-sm">Streckenabschnitte:</h5>
                          <div className="space-y-2">
                            {/* Base to Pickup */}
                            <div className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                              <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center text-sm font-bold">1</div>
                              <div className="flex-1">
                                <p className="text-sm font-medium">Sternstraße 1, Hagen → Abholung</p>
                                <p className="text-xs text-gray-500">
                                  {typeof calculatedPrice.details.routeBreakdown.baseToPickup === 'object' 
                                    ? `${calculatedPrice.details.routeBreakdown.baseToPickup.distance} km · ${calculatedPrice.details.routeBreakdown.baseToPickup.durationText}`
                                    : `${calculatedPrice.details.routeBreakdown.baseToPickup} km`}
                                </p>
                              </div>
                            </div>
                            
                            {/* Pickup to Destination */}
                            <div className="flex items-center gap-3 p-2 bg-blue-50 rounded-lg">
                              <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold">2</div>
                              <div className="flex-1">
                                <p className="text-sm font-medium">Abholung → Ziel (mit Tier)</p>
                                <p className="text-xs text-gray-500">
                                  {typeof calculatedPrice.details.routeBreakdown.pickupToDestination === 'object'
                                    ? `${calculatedPrice.details.routeBreakdown.pickupToDestination.distance} km · ${calculatedPrice.details.routeBreakdown.pickupToDestination.durationText}`
                                    : `${calculatedPrice.details.routeBreakdown.pickupToDestination} km`}
                                </p>
                              </div>
                            </div>
                            
                            {/* Return to Base */}
                            <div className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                              <div className="w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center text-sm font-bold">3</div>
                              <div className="flex-1">
                                <p className="text-sm font-medium">Ziel → Sternstraße 1, Hagen</p>
                                <p className="text-xs text-gray-500">
                                  {typeof calculatedPrice.details.routeBreakdown.returnToBase === 'object'
                                    ? `${calculatedPrice.details.routeBreakdown.returnToBase.distance} km · ${calculatedPrice.details.routeBreakdown.returnToBase.durationText}`
                                    : `${calculatedPrice.details.routeBreakdown.returnToBase} km`}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Short Distance Route Details */}
                      {calculatedPrice.details.routeDetails && (
                        <div className="space-y-2">
                          <h5 className="font-semibold text-gray-700 text-sm">Route:</h5>
                          <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                            <div className="flex flex-col items-center">
                              <div className="w-3 h-3 rounded-full bg-green-500"></div>
                              <div className="w-0.5 h-8 bg-blue-400"></div>
                              <div className="w-3 h-3 rounded-full bg-red-500"></div>
                            </div>
                            <div className="flex-1 space-y-2">
                              <div>
                                <p className="text-xs text-gray-500">Start</p>
                                <p className="text-sm font-medium">{calculatedPrice.details.routeDetails.startAddress}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Ziel</p>
                                <p className="text-sm font-medium">{calculatedPrice.details.routeDetails.endAddress}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-bold text-blue-600">{calculatedPrice.details.routeDetails.distance}</p>
                              <p className="text-sm text-gray-500">{calculatedPrice.details.routeDetails.duration}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Additional Costs */}
                      {(calculatedPrice.details.waitingService > 0 || calculatedPrice.details.disinfectionCost > 0 || calculatedPrice.details.anfahrt) && (
                        <div className="border-t pt-3 space-y-1">
                          <h5 className="font-semibold text-gray-700 text-sm">Zusatzkosten:</h5>
                          {calculatedPrice.details.anfahrt && (
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">Anfahrtspauschale:</span>
                              <span className="font-medium">{calculatedPrice.details.anfahrt}€</span>
                            </div>
                          )}
                          {calculatedPrice.details.waitingService > 0 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">Warteservice:</span>
                              <span className="font-medium">{calculatedPrice.details.waitingService}€</span>
                            </div>
                          )}
                          {calculatedPrice.details.disinfectionCost > 0 && (
                            <div className="flex justify-between text-sm text-orange-700">
                              <span>Desinfektion (infektiös):</span>
                              <span className="font-medium">{calculatedPrice.details.disinfectionCost}€</span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Google Maps Attribution */}
                      {calculatedPrice.details.googleMapsUsed && (
                        <p className="text-xs text-gray-400 text-center pt-2">
                          {calculatedPrice.details.routeType === 'kürzeste Strecke' 
                            ? '📍 Kürzeste Strecke berechnet via Google Maps (Tierschutz-Tarif)'
                            : '🚗 Schnellste Route berechnet via Google Maps'}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex items-start space-x-3 p-4 bg-blue-50 rounded-xl">
              <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-700">
                <strong>Hinweis:</strong> Die Berechnung basiert auf Google Maps Routendaten.<br />
                <strong>Kurzstrecke:</strong> Kürzeste Strecke (Abholung → Ziel)<br />
                <strong>Langstrecke Standard:</strong> Schnellste Route (45€/Std basierend auf Fahrzeit)<br />
                <strong>Langstrecke Tierschutz:</strong> Kürzeste Strecke (0,40€/km)
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default AdvancedPriceCalculator;
